import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Upload, Download, Trash2, Play, ImageIcon, Search, Eye, FileVideo, FileImage, CheckSquare, Square, HardDrive, Calendar, Loader2, Camera, Package } from 'lucide-react';
import {
  ACCEPTED_FORMATS_ATTR, MAX_FILE_SIZE_MB, PAGE_SIZE,
  isImageFile, isVideoFile, formatFileSize, formatDate,
  getMediaUrl, getThumbnailUrl, getMediaName,
} from './videoManagerHelpers';
import { useVideoManager } from './useVideoManager';
import VideoManagerModals from './VideoManagerModals';

const VideoManager = ({ userRole = 'member', showUpload = true, showDelete = true }) => {
  const vm = useVideoManager({ userRole });
  const {
    media, loading, uploading, uploadProgress,
    searchTerm, setSearchTerm, typeFilter, setTypeFilter,
    setDeleteConfirmOpen, setItemToDelete,
    selectedIds, setSelectedIds, setBulkDeleteOpen,
    totalCount, hasMore, loadingMore, sentinelRef,
    fileInputRef,
    handleFileSelect, handleDownload, toggleSelect, clearSelection,
    selectAllVisible, openZipDialog, openPreview, fetchMoreMedia,
    filteredMedia, stats,
  } = vm;

  return (
    <div className="space-y-6" data-testid="photos-videos-manager">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-fuchsia-900 p-6 lg:p-7 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(168,85,247,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(236,72,153,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative flex flex-col xl:flex-row items-start xl:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-rose-400 flex items-center justify-center shadow-lg shadow-purple-500/40 flex-shrink-0">
              <Camera className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-200 border border-pink-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
                  Galerie premium
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm font-semibold text-xs">
                  {totalCount || media.length} fichier{(totalCount || media.length) > 1 ? 's' : ''}
                </span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold">Photos / Vidéos</h2>
              <p className="text-pink-100 mt-1 text-sm lg:text-base">
                Photos (JPEG, PNG, WEBP, HEIC) &amp; vidéos (MP4, WebM, MOV, AVI…) — jusqu&apos;à {MAX_FILE_SIZE_MB} Mo par fichier.
              </p>
            </div>
          </div>
          {showUpload && (
            <div className="flex gap-2 w-full xl:w-auto">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FORMATS_ATTR}
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="media-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white border-0 shadow-lg shadow-pink-500/30 w-full xl:w-auto"
                data-testid="media-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Upload {uploadProgress.index}/{uploadProgress.total} – {uploadProgress.percent}%</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Importer photos / vidéos</>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
            <div className="flex items-center gap-2 text-pink-300 text-xs font-semibold uppercase tracking-wider">
              <FileImage className="h-3.5 w-3.5" /> Photos
            </div>
            <p className="text-3xl font-bold mt-1">{stats.photos}</p>
            <p className="text-xs text-pink-100 mt-0.5">images dans la GED</p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
            <div className="flex items-center gap-2 text-purple-300 text-xs font-semibold uppercase tracking-wider">
              <FileVideo className="h-3.5 w-3.5" /> Vidéos
            </div>
            <p className="text-3xl font-bold mt-1">{stats.videos}</p>
            <p className="text-xs text-pink-100 mt-0.5">vidéos disponibles</p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
            <div className="flex items-center gap-2 text-cyan-300 text-xs font-semibold uppercase tracking-wider">
              <HardDrive className="h-3.5 w-3.5" /> Stockage
            </div>
            <p className="text-3xl font-bold mt-1">{formatFileSize(stats.totalSize)}</p>
            <p className="text-xs text-pink-100 mt-0.5">total cumulé</p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
            <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
              <Upload className="h-3.5 w-3.5" /> Limite/fichier
            </div>
            <p className="text-3xl font-bold mt-1">{MAX_FILE_SIZE_MB} MB</p>
            <p className="text-xs text-pink-100 mt-0.5">par fichier</p>
          </div>
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
            <span className="text-sm font-medium text-purple-700">
              Upload {uploadProgress.index}/{uploadProgress.total} — {uploadProgress.percent}%
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-pink-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Rechercher un fichier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="media-search-input"
          />
        </div>
        <div className="flex gap-1">
          {[
            { key: 'all', label: 'Tous', icon: ImageIcon },
            { key: 'image', label: 'Photos', icon: FileImage },
            { key: 'video', label: 'Vidéos', icon: FileVideo },
          ].map((f) => (
            <Button
              key={f.key}
              variant={typeFilter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter(f.key)}
              className={typeFilter === f.key ? 'bg-purple-600 hover:bg-purple-700 gap-1.5' : 'gap-1.5'}
              data-testid={`media-filter-${f.key}`}
            >
              <f.icon className="h-3.5 w-3.5" /> {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Barre d'actions multi-sélection */}
      {filteredMedia.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const allSelected = filteredMedia.every((m) => selectedIds.has(m.id));
                if (allSelected) {
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    filteredMedia.forEach((m) => next.delete(m.id));
                    return next;
                  });
                } else {
                  selectAllVisible(filteredMedia);
                }
              }}
              className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-purple-700 transition-colors"
              data-testid="media-select-all"
            >
              {filteredMedia.every((m) => selectedIds.has(m.id)) ? (
                <CheckSquare className="h-4 w-4 text-purple-600" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              <span className="font-medium">
                {selectedIds.size > 0
                  ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}`
                  : 'Tout sélectionner'}
              </span>
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={clearSelection}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="media-clear-selection"
              >
                Effacer la sélection
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={selectedIds.size === 0}
              onClick={openZipDialog}
              className="gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-40"
              data-testid="media-create-zip-btn"
            >
              <Package className="h-4 w-4" />
              Créer un dossier ZIP
              {selectedIds.size > 0 && <Badge className="bg-purple-600 text-white ml-1 font-semibold">{selectedIds.size}</Badge>}
            </Button>
            {showDelete && (
              <Button
                size="sm"
                variant="outline"
                disabled={selectedIds.size === 0}
                onClick={() => setBulkDeleteOpen(true)}
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                data-testid="media-bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
                {selectedIds.size > 0 && <Badge className="bg-red-600 text-white ml-1 font-semibold">{selectedIds.size}</Badge>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Aucun fichier trouvé' : 'Aucun fichier'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Importez vos premières photos ou vidéos'}
            </p>
            {!searchTerm && showUpload && (
              <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700">
                <Upload className="h-4 w-4 mr-2" /> Importer
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-grid">
          {filteredMedia.map((item) => {
            const img = isImageFile(item);
            const vid = isVideoFile(item);
            const url = getMediaUrl(item);
            const isSelected = selectedIds.has(item.id);
            return (
              <Card
                key={item.id}
                className={`overflow-hidden hover:shadow-lg transition-all group ${
                  isSelected ? 'ring-2 ring-purple-500 shadow-lg' : ''
                }`}
                data-testid={`media-card-${item.id}`}
              >
                <div
                  className="relative aspect-square bg-gray-900 cursor-pointer overflow-hidden"
                  onClick={() => openPreview(item)}
                >
                  {/* Checkbox sélection */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(item.id);
                    }}
                    className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                      isSelected
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white/80 backdrop-blur-sm text-slate-700 hover:bg-white opacity-0 group-hover:opacity-100'
                    }`}
                    data-testid={`media-select-${item.id}`}
                    aria-label="Sélectionner ce fichier"
                  >
                    {isSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                  {img ? (
                    <img
                      src={getThumbnailUrl(item) || url}
                      alt={getMediaName(item)}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallback !== '1') {
                          e.currentTarget.dataset.fallback = '1';
                          e.currentTarget.src = url;
                        } else {
                          e.currentTarget.style.display = 'none';
                        }
                      }}
                    />
                  ) : vid ? (
                    <>
                      {getThumbnailUrl(item) ? (
                        <img
                          src={getThumbnailUrl(item)}
                          alt={getMediaName(item)}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={url}
                          muted
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="h-5 w-5 text-white ml-0.5" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <FileVideo className="h-10 w-10 text-gray-500" />
                    </div>
                  )}
                  <Badge
                    className={`absolute top-2 right-2 text-[10px] font-semibold ${
                      img ? 'bg-pink-600' : 'bg-purple-600'
                    } text-white`}
                  >
                    {img ? 'Photo' : 'Vidéo'}
                  </Badge>
                </div>
                <CardContent className="p-2.5">
                  <h3 className="font-medium text-xs truncate mb-1" title={getMediaName(item)}>
                    {getMediaName(item)}
                  </h3>
                  <p className="text-[10px] text-gray-500 flex items-center gap-1 mb-2">
                    <HardDrive className="h-2.5 w-2.5" />
                    {formatFileSize(item.file_size || item.taille)}
                    <span className="text-gray-300">·</span>
                    <Calendar className="h-2.5 w-2.5" />
                    {formatDate(item.uploaded_at || item.created_at)}
                  </p>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-7 text-[10px] px-2"
                      onClick={() => openPreview(item)}
                      data-testid={`media-view-${item.id}`}
                    >
                      <Eye className="h-3 w-3 mr-0.5" /> Voir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      onClick={() => handleDownload(item)}
                      data-testid={`media-download-${item.id}`}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                    {showDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setItemToDelete(item);
                          setDeleteConfirmOpen(true);
                        }}
                        data-testid={`media-delete-${item.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sentinel infinite scroll + indicateurs */}
      {!loading && filteredMedia.length > 0 && (
        <div className="flex flex-col items-center py-6 gap-2" data-testid="media-pagination-zone">
          <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
          {loadingMore && (
            <div className="flex items-center gap-2 text-sm text-purple-600" data-testid="media-loading-more">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement…
            </div>
          )}
          {!hasMore && totalCount > 0 && (
            <p className="text-xs text-gray-400" data-testid="media-end-reached">
              {filteredMedia.length} affichés sur {totalCount} fichier{totalCount > 1 ? 's' : ''} · Fin de la liste
            </p>
          )}
          {hasMore && !loadingMore && (
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchMoreMedia}
              className="text-purple-700 hover:text-purple-900"
              data-testid="media-load-more-btn"
            >
              Charger {Math.min(PAGE_SIZE, totalCount - filteredMedia.length)} fichier(s) de plus
            </Button>
          )}
        </div>
      )}

      <VideoManagerModals userRole={userRole} {...vm} />
    </div>
  );
};

export default VideoManager;
